<template>
    <g-base-box padding="none" border-radius="large">
        <g-media-object vertical-align="center" class="activation-file-container">
            <template #left>
                <div class="activation-file-image-container">
                    <img v-if="isImage && thumbnail" :src="thumbnail" alt="file" class="activation-file-image" />
                    <g-icon v-else :name="icon" color="grey-5" data-testid="file-icon" />
                </div>
            </template>
            <template #default>
                <div class="activation-file-info">
                    <g-typography tag="p" color="info" :value="headerText" />
                    <div>
                        <a :href="url" target="_blank">{{ fileName }}</a>
                    </div>
                    <g-typography
                        v-if="date"
                        tag="p"
                        size="small"
                        color="info"
                        :value="formattedDate"
                        data-testid="date"
                    />
                </div>
            </template>
            <template v-if="deletable" #right>
                <g-button
                    class="g-mr-3"
                    theme="danger"
                    :border="false"
                    icon="trash-alt"
                    icon-size="small"
                    data-testid="delete-button"
                    @click="showDeleteModal"
                />
            </template>
        </g-media-object>
        <g-base-box v-if="isContentManualStatsEnabled" :border="false" padding="p-3">
            <g-button
                v-if="!isManualMetricsVisible"
                shape="round"
                theme="live"
                block
                type="default"
                class="margin-bottom-normal"
                @click.prevent="handleUpdateMetrics"
            >
                Update
            </g-button>
            <update-content-manual-stats
                v-if="isManualMetricsVisible"
                :content-id="contentId"
                :network-id="networkId"
                class="g-mt-4"
                @cancel="toggleManualMetricsVisible"
            />
        </g-base-box>
        <generic-confirmation-modal
            :visible="isDeleteModalVisible"
            confirm-button-text="Yes"
            :closable="false"
            @cancel="hideDeleteModal"
            @continue="handleFileDelete"
        >
            <template #title> Delete file? </template>
            You're about to delete {{ fileName }}. This cannot be undone.
        </generic-confirmation-modal>
    </g-base-box>
</template>

<script>
    import moment from 'moment';
    import { computed, toRefs } from 'vue-demi';
    import FILE_ICON_NAMES from '../../constants/file-icon-names';
    import FILE_DISPLAY_NAMES from '../../constants/file-display-names';
    import useToggle from '../../../src/composables/use-toggle';
    import useNetworks from '../../../src/composables/use-networks';
    import useRouter from '../../../src/composables/use-router';
    import { useFeatureFlagsStore } from '../../../stores';
    import GenericConfirmationModal from '../data-views/activation-briefs/partials/GenericConfirmationModal.vue';
    import UpdateContentManualStats from '../data-views/content/UpdateContentManualStats.vue';

    export default {
        name: 'BaseActivationPiece',
        components: {
            GenericConfirmationModal,
            UpdateContentManualStats
        },
        props: {
            headerText: {
                type: String,
                default: FILE_DISPLAY_NAMES.default
            },
            icon: {
                type: String,
                default: FILE_ICON_NAMES.default
            },
            date: {
                type: String,
                default: undefined
            },
            isImage: {
                type: Boolean,
                default: false
            },
            thumbnail: {
                type: String,
                default: undefined
            },
            url: {
                type: String,
                default: undefined,
                required: true
            },
            fileName: {
                type: String,
                default: undefined,
                required: true
            },
            fileDeleteName: {
                type: String,
                default: undefined
            },
            deletable: {
                type: Boolean,
                default: false
            },
            contentId: {
                type: Number,
                default: null
            },
            networkId: {
                type: Number,
                default: null
            },
            hideActions: {
                type: Boolean,
                default: false
            }
        },

        setup(props, { emit }) {
            const router = useRouter().get();
            const { date, fileDeleteName, networkId, hideActions } = toRefs(props);
            const { isMetaById, isInstagramById } = useNetworks();

            const isMeta = computed(() => isMetaById(networkId.value));
            const isInstagram = computed(() => isInstagramById(networkId.value));

            const isContentManualStatsEnabled = computed(
                () =>
                    (!hideActions.value &&
                        isMeta.value &&
                        useFeatureFlagsStore().data['content-manual-stats-enabled']) ||
                    false
            );

            const useContentMetricScreenshots = computed(
                () => (isInstagram.value && useFeatureFlagsStore().data['creator-content-metric-screenshots']) || false
            );

            const {
                currentValue: isDeleteModalVisible,
                setTrue: showDeleteModal,
                setFalse: hideDeleteModal
            } = useToggle(false);

            const {
                currentValue: isManualMetricsVisible,
                setTrue: showManualMetrics,
                setFalse: hideManualMetrics
            } = useToggle(false);

            const formattedDate = computed(() => {
                if (!date.value) {
                    return '';
                }

                return moment(date.value).format('MMM D YYYY h:mm A');
            });

            const handleFileDelete = () => {
                emit('file-deleted', fileDeleteName.value);
                hideDeleteModal();
            };

            const toggleManualMetricsVisible = () => {
                isManualMetricsVisible.value ? hideManualMetrics() : showManualMetrics();
            };

            const handleUpdateMetrics = () => {
                if (useContentMetricScreenshots.value) {
                    router?.push({ name: 'v2.activation.brief.task.content' });
                } else {
                    toggleManualMetricsVisible();
                }
            };

            return {
                isDeleteModalVisible,
                isContentManualStatsEnabled,
                isManualMetricsVisible,
                formattedDate,
                showDeleteModal,
                hideDeleteModal,
                handleFileDelete,
                handleUpdateMetrics,
                toggleManualMetricsVisible
            };
        }
    };
</script>

<style lang="scss" scoped>
    ::v-deep .media-object-content {
        overflow: hidden;
    }

    .activation-file-container {
        max-height: 95px;
        width: 100%;
    }

    .activation-file-image-container {
        width: 93px;
        height: 93px;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: $g-color-grey-3;
        border-right: 1px solid $g-color-grey-4;
    }

    .activation-file-image {
        width: 93px;
        height: 93px;
        object-fit: cover;
    }

    .activation-file-info {
        padding: $g-spacing-3;
    }

    .activation-file-info div {
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;
        width: 100%;
    }
</style>
